import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Parses a date input (string, Date object, or timestamp number) into a JavaScript Date object.
 * Handles common formats like YYYYMMDD, YYYY-MM-DD, and standard ISO strings.
 * @param dateInput The date input to parse.
 * @returns A Date object if parsing is successful, otherwise null.
 */
export function parseDateString(dateInput: string | Date | number | undefined | null): Date | null {
  if (dateInput === null || typeof dateInput === 'undefined') {
    return null;
  }
  if (dateInput instanceof Date) {
    return !isNaN(dateInput.getTime()) ? dateInput : null;
  }
  if (typeof dateInput === 'number') {
    const d = new Date(dateInput);
    return !isNaN(d.getTime()) ? d : null;
  }
  if (typeof dateInput === 'string') {
    // Try YYYYMMDD
    if (/^\d{8}$/.test(dateInput)) {
      const year = parseInt(dateInput.substring(0, 4), 10);
      const month = parseInt(dateInput.substring(4, 6), 10) - 1; // Month is 0-indexed
      const day = parseInt(dateInput.substring(6, 8), 10);
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        const d = new Date(year, month, day);
        return !isNaN(d.getTime()) ? d : null;
      }
    }
    // Try YYYY-MM-DD (and allow for YYYY-M-D, etc. by new Date())
    // Also handles full ISO strings
    const d = new Date(dateInput);
    return !isNaN(d.getTime()) ? d : null;
  }
  return null;
}

/**
 * Smartly formats a date for display on a chart axis based on the overall series duration.
 * @param currentDate The Date object to format.
 * @param minSeriesDate The earliest Date in the entire series.
 * @param maxSeriesDate The latest Date in the entire series.
 * @param locale User's locale string (e.g., 'en-US'), defaults to undefined (system locale).
 * @returns A formatted date string.
 */
export function smartFormatDateDisplay(
  currentDate: Date,
  minSeriesDate: Date,
  maxSeriesDate: Date,
  locale: string | undefined = undefined
): string {
  if (
    isNaN(currentDate.getTime()) ||
    isNaN(minSeriesDate.getTime()) ||
    isNaN(maxSeriesDate.getTime())
  ) {
    // Fallback for invalid dates
    return currentDate instanceof Date ? currentDate.toLocaleDateString(locale) : String(currentDate);
  }

  const durationMs = maxSeriesDate.getTime() - minSeriesDate.getTime();
  const ONE_HOUR_MS = 60 * 60 * 1000;
  const ONE_DAY_MS = 24 * ONE_HOUR_MS;
  const TWO_DAYS_MS = 2 * ONE_DAY_MS;
  const ONE_WEEK_MS = 7 * ONE_DAY_MS;
  const TWO_WEEKS_MS = 2 * ONE_WEEK_MS;
  const NINETY_DAYS_MS = 90 * ONE_DAY_MS; // Approx 3 months
  const ONE_YEAR_MS = 365 * ONE_DAY_MS; // Approx 1 year
  const EIGHTEEN_MONTHS_MS = Math.floor(1.5 * ONE_YEAR_MS);

  // Series duration less than 2 days: show time if dates are different, or day + time if same day
  if (durationMs < TWO_DAYS_MS) {
    // If data points span across midnight or are within the same day but have time precision
    if (currentDate.getHours() !== 0 || currentDate.getMinutes() !== 0 || currentDate.getSeconds() !== 0 || durationMs < ONE_DAY_MS) {
       // Check if min and max are on the same calendar day
      if (minSeriesDate.getFullYear() === maxSeriesDate.getFullYear() &&
          minSeriesDate.getMonth() === maxSeriesDate.getMonth() &&
          minSeriesDate.getDate() === maxSeriesDate.getDate()) {
        return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(currentDate); // "2:30 PM"
      } else {
        return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(currentDate); // "Jun 5, 2:30 PM"
      }
    }
    // Default for day-level data spanning < 2 days
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(currentDate); // "Jun 5"
  }

  // Series duration less than 2 weeks
  if (durationMs < TWO_WEEKS_MS) {
    return new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric' }).format(currentDate); // "Mon, Jun 5"
  }

  // Series duration less than ~3 months (90 days)
  if (durationMs < NINETY_DAYS_MS) {
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(currentDate); // "Jun 5"
  }

  // Series duration less than ~1.5 years
  if (durationMs < EIGHTEEN_MONTHS_MS) {
    if (minSeriesDate.getFullYear() === maxSeriesDate.getFullYear()) {
      // All within the same year
      return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(currentDate); // "Jun 5"
    } else {
      return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: '2-digit' }).format(currentDate); // "Jun 5, '24"
    }
  }

  // Series duration longer than 1.5 years
  return new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }).format(currentDate); // "Jun 2024"
}
